// AM drill-down — click an Account Manager row on the dashboard scoreboard
// to get the coaching view (Ali review 2026-07-07): 12-week activity trend,
// follow-up discipline, oldest untouched bids, loss reasons, customer
// coverage, and realized-vs-target pace. Sales-side only; read-only.
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtMoney, fmtMoneyK, fmtDate, fmtDateTime, useIsMobile } from './UI';

const TIER_LABELS = {
  breakeven_value: 'Breakeven', bogey_value: 'Bogey', par_value: 'Par',
  birdie_value: 'Birdie', eagle_value: 'Eagle', hole_in_one_value: 'Hole in One',
};

export default function AmDrillModal({ amId, year, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    setData(null); setError('');
    api.amDrill(amId, year && /^\d{4}$/.test(String(year)) ? { year } : {})
      .then(setData).catch(e => setError(e.message));
  }, [amId, year]);

  // Fill the 12-week axis so quiet weeks render as explicit zeros — a gap
  // that looks like data is how activity droughts hide.
  const weeks = useMemo(() => {
    if (!data) return [];
    const byWeek = {};
    for (const w of data.activity_trend) byWeek[String(w.week_of).slice(0, 10)] = Number(w.n);
    const out = [];
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    for (let i = 11; i >= 0; i--) {
      const d = new Date(monday);
      d.setDate(monday.getDate() - i * 7);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      out.push({ key, label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), n: byWeek[key] || 0 });
    }
    return out;
  }, [data]);

  const maxN = Math.max(1, ...weeks.map(w => w.n));

  // Pace vs Breakeven, same math as the Team Pace Board.
  const pace = useMemo(() => {
    if (!data?.targets) return null;
    const be = Number(data.targets.breakeven_value) || 0;
    if (!be) return null;
    const start = new Date(data.year, 0, 1).getTime();
    const elapsed = Math.min(1, (Date.now() - start) / (365 * 86400000));
    return data.realized / (be * elapsed);
  }, [data]);

  const attained = useMemo(() => {
    if (!data?.targets) return null;
    let hit = null;
    for (const [key, label] of Object.entries(TIER_LABELS)) {
      const v = Number(data.targets[key]) || 0;
      if (v > 0 && data.realized >= v) hit = label;
    }
    return hit;
  }, [data]);

  const secTitle = { fontSize: 11, fontWeight: 800, color: colors.orange, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '14px 0 6px' };
  const statBox = { flex: '1 1 110px', background: '#F9FAFB', border: `1px solid ${colors.border}`, borderRadius: 8, padding: '8px 12px' };
  const statVal = (v, bad) => ({ fontSize: 18, fontWeight: 800, color: bad ? colors.red : '#1F2937' });

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 10, width: 680, maxWidth: '96vw',
        maxHeight: '92vh', overflowY: 'auto', padding: isMobile ? 14 : 20,
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
      }}>
        {!data && !error && <div style={{ color: colors.textMuted, padding: 20 }}>Loading…</div>}
        {error && <div style={styles.errorBox}>{error}</div>}
        {data && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ ...styles.h2, margin: 0 }}>{data.am.name}</h2>
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                  {data.am.branch || '—'} · {data.year}
                  {data.am.last_active_at ? ` · last active ${fmtDateTime(data.am.last_active_at)}` : ' · never logged in'}
                </div>
              </div>
              <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
            </div>

            {/* Pace snapshot */}
            <div style={secTitle}>Revenue vs Target</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div style={statBox}>
                <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' }}>Realized YTD</div>
                <div style={statVal(data.realized)}>{fmtMoneyK(data.realized)}</div>
              </div>
              <div style={statBox}>
                <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' }}>Tier Attained</div>
                <div style={{ ...statVal(0), color: attained ? colors.green : colors.textMuted }}>{data.targets ? (attained || 'Below BE') : 'no targets'}</div>
              </div>
              <div style={statBox} title="Realized ÷ (Breakeven × fraction of year elapsed)">
                <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' }}>Pace vs Breakeven</div>
                <div style={{ ...statVal(0), color: pace == null ? colors.textMuted : pace >= 1 ? colors.green : pace >= 0.8 ? '#B45309' : colors.red }}>
                  {pace == null ? '—' : `${Math.round(pace * 100)}%`}
                </div>
              </div>
            </div>

            {/* Activity trend */}
            <div style={secTitle}>Activity — last 12 weeks</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 74 }}>
              {weeks.map(w => (
                <div key={w.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 0 }}
                  title={`Week of ${w.label}: ${w.n} activit${w.n === 1 ? 'y' : 'ies'}`}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: w.n === 0 ? colors.red : colors.textMuted }}>{w.n}</span>
                  <div style={{
                    width: '100%', maxWidth: 26, borderRadius: '3px 3px 0 0',
                    height: Math.max(3, Math.round((w.n / maxN) * 48)),
                    background: w.n === 0 ? '#FEE2E2' : colors.orange,
                  }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: colors.textMuted }}>
              <span>{weeks[0]?.label}</span><span>{weeks[weeks.length - 1]?.label} (this wk)</span>
            </div>

            {/* Follow-up discipline + coverage */}
            <div style={secTitle}>Follow-up Discipline & Coverage</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div style={statBox}>
                <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' }}>Overdue</div>
                <div style={statVal(0, data.followups.overdue > 0)}>{data.followups.overdue}</div>
              </div>
              <div style={statBox} title="Open entries with no next follow-up date scheduled at all">
                <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' }}>Unscheduled</div>
                <div style={statVal(0, data.followups.unscheduled > 0)}>{data.followups.unscheduled}</div>
              </div>
              <div style={statBox}>
                <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' }}>Due next 7d</div>
                <div style={statVal(0)}>{data.followups.due_next_7}</div>
              </div>
              <div style={statBox} title="Customers CRM-assigned to this AM; stale = no call-log touch by anyone in 60 days">
                <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' }}>Book / Stale</div>
                <div style={statVal(0, data.coverage.stale > 0)}>
                  {data.coverage.book}<span style={{ fontSize: 13, color: data.coverage.stale > 0 ? colors.red : colors.textMuted }}> / {data.coverage.stale}</span>
                </div>
              </div>
            </div>

            {/* Oldest open bids */}
            <div style={secTitle}>Oldest Untouched Bids</div>
            {data.oldest_bids.length === 0
              ? <div style={{ fontSize: 12, color: colors.textMuted }}>No open bids.</div>
              : data.oldest_bids.map((b, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12.5, padding: '4px 0', borderBottom: '1px solid #F3F4F6', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, minWidth: 0 }}>{b.site}</span>
                  <span style={{ color: colors.textMuted, whiteSpace: 'nowrap' }}>
                    {fmtMoney(b.estimated_value)} · {b.win_pct != null ? `${b.win_pct}% · ` : ''}
                    <strong style={{ color: b.days_since_update > 30 ? colors.red : colors.textMuted }}>{b.days_since_update}d idle</strong>
                  </span>
                </div>
              ))}

            {/* Loss reasons */}
            <div style={secTitle}>Loss Reasons ({data.year})</div>
            {data.loss_reasons.length === 0
              ? <div style={{ fontSize: 12, color: colors.textMuted }}>No lost deals in {data.year}.</div>
              : data.loss_reasons.map((l, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12.5, padding: '4px 0', borderBottom: '1px solid #F3F4F6' }}>
                  <span>{l.reason}</span>
                  <span style={{ color: colors.textMuted, whiteSpace: 'nowrap' }}>{l.n} deal{l.n === 1 ? '' : 's'} · {fmtMoneyK(l.value)}</span>
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
}
